import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from '@thd-olt-component-react/router';
import { Profile, Location, Ruler } from '@one-thd/sui-icons';
import { BadgeRenderer } from '../Badges/BadgeRenderer';
import { ProfileImage } from '../../common/ProfileImage/ProfileImage';
import { FavoriteIcon } from '../../common/FavoriteIcon/FavoriteIcon';
import { composeLocationText, formattedYearOfExperience } from '../../../utils/formatters';
import { AvailabilityStatus } from '../../common/AvailabilityStatus/AvailabilityStatus';
import { useStore } from '../../../store';
import ProfileShowMoreButton from './ProfileShowMoreButton';
import TradeTypesRender from './TradeTypesRender';
import OverflowTooltip from '../../common/OverflowTooltip/OverflowTooltip';
import { EXP_ROUTES } from '../../../utils/constants';
import { composeGoBackUrlParam } from '../../../utils/go-back';

const ProfileGridItem = ({ candidate }) => {
  const {
    candidateId, name, zipCode, tradeTypes, city, state, yearsOfExperience,
    commuteDistance, availability, availableFrom
  } = candidate;

  const {
    favoritesStore: {
      removeFromFavorites
    }
  } = useStore();
  const ref = useRef(null);

  const distanceText = {
    'less than 5 miles': '< 5 miles',
    'up to 10 miles': '< 10 miles',
    'up to 25 miles': '< 25 miles',
    '50 miles or more': '> 50 miles'
  };

  return (
    <section
      data-testid={`profile-search-card-${candidateId}`}
      tabIndex={0}
      className="ptp-search-grid-item__root"
      aria-label={'grid-item__' + candidateId}
    >
      <div
        className="ptp-search-grid-item__name-section"
        data-testid="profile-search-card-name-section"
        ref={ref}
      >
        <div className="ptp-search-grid-item__availability_header">
          <AvailabilityStatus
            className="ptp-search-grid-item__availability"
            availability={availability}
            availableFrom={availableFrom}
          />
          <div className="sui-ml-auto">
            <FavoriteIcon candidate={candidate} callback={() => removeFromFavorites(candidateId)} />
          </div>
        </div>
        <div
          className="ptp-search-grid-item__name_and_image_container"
        >
          <div className="ptp-search-grid-item__name_container">
            {name && (
              <OverflowTooltip
                byHeight
              >
                <Link
                  to={EXP_ROUTES.SEARCH_PROFILE(candidateId) + composeGoBackUrlParam()}
                  className="ptp-search-grid-item__name"
                  data-testid="profile-name-btn"
                >
                  {name}
                </Link>
              </OverflowTooltip>
            )}

            <div className="ptp-search-grid-item__location-section">
              <div className="ptp-search-grid-item__location-section-row">
                {(zipCode || city || state)
                  && (
                    <OverflowTooltip aria-label="location" className="ptp-search-grid-item__zip">
                      <span className="ptp-search-grid-item__zip-icon"><Location size="small" /></span>
                      <span className="ptp-search-grid-item__zip-text">{composeLocationText(zipCode, city, state)}</span>
                    </OverflowTooltip>
                  )}
              </div>
              <div className="ptp-search-grid-item__location-section-row">
                {commuteDistance
                  && (
                    <div data-testid="profile-search-card-commute-distance" className="ptp-search-grid-item__location-section-work-radius">
                      <img
                        src="https://assets.thdstatic.com/images/v1/path-to-pro/work-radius-icon.svg"
                        alt="Work radius"
                        className="ptp-work-radius-icon"
                      />
                      <span>Work Radius: {distanceText[commuteDistance]}</span>
                    </div>
                  )}
              </div>
              <div className="ptp-search-grid-item__location-section-row">
                {yearsOfExperience && (
                  <div className="ptp-search-grid-item__location-section-ruler">
                    <Ruler size="small" />
                    <div
                      className="ptp-search-grid-item__years-of-experience"
                      data-testid="profile-search-card-skillbar"
                    >
                      {formattedYearOfExperience(yearsOfExperience)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="ptp-search-grid-item__content-profile-image-container">
            {candidate.profilePicture ? <ProfileImage profilePicture={candidate.profilePicture} /> : <Profile className="slm-profile-image-with-favorite-icon__default-image" />}
          </div>
        </div>
        <div className="ptp-search-grid-item__experience-section ">
          {tradeTypes && (
            <TradeTypesRender candidateId={candidateId} tradeTypes={tradeTypes} />
          )}
        </div>
        <ul className="ptp-search-grid-item__badges ptp-hidden__mobile-down">
          <p className="ptp-search-grid-item__badges-title">Path To Pro Badges</p>
          <BadgeRenderer profileData={candidate} badgeSize="30px" />
        </ul>
        <ProfileShowMoreButton item={candidate} />
      </div>
    </section>
  );
};

ProfileGridItem.propTypes = {
  candidate: PropTypes.shape({
    candidateId: PropTypes.number,
    city: PropTypes.string,
    state: PropTypes.string,
    name: PropTypes.string,
    tradeTypes: PropTypes.arrayOf(PropTypes.string),
    yearsOfExperience: PropTypes.string,
    zipCode: PropTypes.string
  }).isRequired
};

export default ProfileGridItem;
